/**
 * Agency HQ as a command console: a dark screen with a faint grid, a status
 * line, their next move asked in big type, where they stand (missions filed,
 * the door's seven locks), and three moves as glass panels: the next
 * mission (their case's laptop, the Enemy's seal on it), pass on a key (their
 * key, their name on it once engraved), and their legend. (Their circle comes
 * back as a fourth once it exists.)
 */
import { useEffect, useId, useState, type ReactNode } from "react";
import { Link2 } from "lucide-react";
import { LOCK_WORDS } from "./vault-clips";
import keyImg from "./key-engrave.webp";
import { OPEN_CASES, screenFor } from "./open-case";
import { EnemyScreen } from "./laptop";
import { engravedName, useMemberName } from "./operative-name";
import { SealedCase } from "./sealed-case";
import type { Tier } from "@/features/funnel/types";

/** The door's locks. */
const LOCKS = LOCK_WORDS.length;
/** How long they can look before the panels pulse to say "tap one". */
const HINT_MS = 8000;

/** One move: a glass panel with gold corner brackets, lifting on hover. */
function Panel({
  label,
  sub,
  onClick,
  href,
  wide,
  children,
}: {
  label: string;
  sub: string;
  onClick?: () => void;
  href?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  const subId = useId();
  const cls = `group relative flex ${wide ? "col-span-2 flex-row items-stretch" : "flex-col"} cursor-pointer overflow-hidden rounded-[10px] border border-[#d6a94e]/30 bg-[linear-gradient(160deg,rgba(214,169,78,.1),rgba(10,11,14,.85)_55%)] text-left shadow-[0_10px_30px_rgba(0,0,0,.45),inset_0_1px_0_rgba(255,255,255,.05)] backdrop-blur-[2px] transition-[translate,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-[#d6a94e]/70 hover:shadow-[0_10px_30px_rgba(0,0,0,.45),0_0_22px_rgba(214,169,78,.25)]`;
  const body = (
    <>
      {/* corner brackets */}
      {[
        "top-1.5 left-1.5 border-t border-l",
        "top-1.5 right-1.5 border-t border-r",
        "bottom-1.5 left-1.5 border-b border-l",
        "right-1.5 bottom-1.5 border-r border-b",
      ].map((c) => (
        <span
          key={c}
          className={`pointer-events-none absolute z-[2] size-3 border-[#d6a94e] ${c}`}
        />
      ))}
      {children}
      <span
        className={`relative z-[1] block px-3.5 ${wide ? "flex flex-1 flex-col justify-center py-3" : "pt-1.5 pb-2.5"}`}
      >
        <span className="block font-stencil text-[16px] leading-tight tracking-[.06em] text-[#f4eddb] uppercase">
          {label}
        </span>
        <span
          id={subId}
          className="mt-0.5 block text-[12.5px] leading-snug text-[#a99d86]"
        >
          {sub}
        </span>
      </span>
    </>
  );
  return href ? (
    <a href={href} aria-label={label} aria-describedby={subId} className={cls}>
      {body}
    </a>
  ) : (
    <button
      type="button"
      aria-label={label}
      aria-describedby={subId}
      onClick={onClick}
      className={cls}
    >
      {body}
    </button>
  );
}

/** A mission's clock on the laptop's screen: the time left, ticking. */
function MissionClock({ endsAt }: { endsAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const left = Math.max(0, endsAt - now);
  const pad = (n: number) => String(n).padStart(2, "0");
  const h = Math.floor(left / 3_600_000);
  const m = Math.floor(left / 60_000) % 60;
  const sec = Math.floor(left / 1000) % 60;
  return (
    <span className="absolute inset-0 grid place-content-center bg-[radial-gradient(circle,#1a1206,#000_75%)] text-center font-mono text-[#ffb547] [text-shadow:0_0_1.2cqw_#ff9a1a]">
      <span className="text-[2.2cqw] tracking-[.3em]">TIME LEFT</span>
      <span className="text-[6cqw] leading-none font-bold tabular-nums">
        {pad(h)}:{pad(m)}:{pad(sec)}
      </span>
    </span>
  );
}

export function HqConsole({
  title,
  message,
  missionCount,
  onRetryFile,
  onNextMission,
  nextMissionLabel,
  nextMissionHint,
  ongoing,
  onContinue,
  sealed,
  comeback,
  onLegend,
  onDoor,
}: {
  title: string;
  message: string;
  /** null until their file is read: shown as a dash, never as 0 */
  missionCount: number | null;
  /** their file failed to load: offer to try again */
  onRetryFile?: () => void;
  onNextMission: () => void;
  nextMissionLabel?: string;
  nextMissionHint?: string;
  /** a mission accepted and not reported yet: the panel continues it */
  ongoing?: { name: string; endsAt: number; tier: Tier } | null;
  onContinue: () => void;
  /** tomorrow's case, sealed before they left: it waits on the panel */
  sealed?: { label: string; at: number } | null;
  /** the Comeback Loop's reasons to come back: the next lock's word (and
   *  when it opens), how the last one felt, days in a row */
  comeback?: {
    nextLock: string | null;
    opensAt: number | null;
    lastFelt: { before: string; after: string } | null;
    streak: number;
    /** their clearance link, to share (copied on tap) */
    clearance?: () => Promise<string>;
  } | null;
  onLegend: () => void;
  /** see their door as it stands */
  onDoor: () => void;
}) {
  const [engraved] = useState(engravedName);
  const { data: memberName } = useMemberName();
  // the account's name when this browser never engraved one (another device,
  // or signed in)
  const name = engraved || memberName || "";
  const [hint, setHint] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setHint(true), HINT_MS);
    return () => window.clearTimeout(t);
  }, []);
  const locks = Math.min(missionCount ?? 0, LOCKS);
  const count = missionCount ?? "—";
  // the countdown to the next lock, ticking by the minute
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!comeback?.opensAt) return;
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, [comeback?.opensAt]);
  const leftMs = comeback?.opensAt ? comeback.opensAt - now : 0;
  const countdown =
    leftMs > 0
      ? `${Math.floor(leftMs / 3_600_000)} h ${Math.floor(leftMs / 60_000) % 60} m`
      : "";
  const [copied, setCopied] = useState(false);
  const copyClearance = async () => {
    if (!comeback?.clearance) return;
    try {
      const link = await comeback.clearance();
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      /* the link is shown in the dossier route either way */
    }
  };

  return (
    <div className="relative flex h-full w-full flex-col overflow-x-hidden overflow-y-auto bg-[#06070a] px-4 pt-[max(14px,2dvh)] pb-6 font-sans text-[#f4eddb]">
      {/* the screen: a faint grid, scanlines */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(214,169,78,.06)_1px,transparent_1px),linear-gradient(90deg,rgba(214,169,78,.06)_1px,transparent_1px)] bg-[size:28px_28px]" />
      <div className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(0,0,0,.18)_0_1px,transparent_1px_3px)]" />

      {/* status line */}
      <div className="relative flex items-center justify-between font-mono text-[10.5px] tracking-[.24em] text-[#d6a94e] uppercase">
        <span className="text-[#a99d86]">
          {comeback && comeback.streak >= 2
            ? `Day ${comeback.streak} in a row`
            : ""}
        </span>
        <span className="text-[#a99d86]">
          {name ? `Operative ${name}` : "Operative"}
        </span>
      </div>

      {/* the question */}
      <div className="relative mt-3 text-center">
        <div className="font-mono text-[11px] tracking-[.3em] text-[#d6a94e] uppercase [text-shadow:0_0_12px_rgba(214,169,78,.5)]">
          ▸ {title} ◂
        </div>
        <h1 className="mx-auto mt-2 max-w-[420px] font-stencil text-[clamp(21px,6.4vw,30px)] leading-[1.08] tracking-[.03em] text-[#f4eddb] uppercase [text-shadow:0_0_22px_rgba(214,169,78,.25)]">
          {message}
        </h1>
      </div>

      {/* where they stand; tap to see their door */}
      <button
        type="button"
        onClick={onDoor}
        disabled={missionCount === null}
        aria-label="See your door"
        className="relative mx-auto mt-3 flex w-full max-w-[460px] shrink-0 cursor-pointer items-center justify-between rounded-[10px] border border-[#d6a94e]/25 bg-black/40 px-4 py-2 text-left transition-colors hover:border-[#d6a94e]/60"
      >
        <div>
          <div className="font-mono text-[10px] tracking-[.22em] text-[#a99d86] uppercase">
            Missions filed
          </div>
          <div className="text-[22px] leading-tight font-bold text-[#d6a94e] tabular-nums">
            {count}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] tracking-[.22em] text-[#a99d86] uppercase">
            Door locks {missionCount === null ? "—" : locks}/{LOCKS}
          </div>
          <div className="mt-1.5 flex justify-end gap-1">
            {Array.from({ length: LOCKS }, (_, i) => (
              <span
                key={i}
                className={`h-2.5 w-4 rounded-[2px] ${
                  i < locks
                    ? "bg-[#d6a94e] shadow-[0_0_8px_rgba(214,169,78,.8)]"
                    : "bg-white/10"
                }`}
              />
            ))}
          </div>
        </div>
      </button>
      {/* why come back: the next lock's word with a countdown, and what
          the last one felt like, in their own words */}
      {comeback && (comeback.nextLock || comeback.lastFelt) ? (
        <div className="relative mx-auto mt-2 flex w-full max-w-[460px] items-center justify-between gap-3 rounded-[10px] border border-[#d6a94e]/20 bg-[#d6a94e]/5 px-4 py-2 font-mono text-[10.5px] tracking-[.14em] text-[#a99d86] uppercase">
          {comeback.nextLock ? (
            <span>
              Next lock{" "}
              <span className="font-bold text-[#d6a94e]">
                {comeback.nextLock}
              </span>
              {countdown ? (
                <span className="text-[#f4eddb]"> · opens in {countdown}</span>
              ) : comeback.opensAt ? (
                <span className="text-[#4ade80]"> · open now</span>
              ) : null}
            </span>
          ) : (
            <span />
          )}
          {comeback.lastFelt ? (
            <span className="shrink-0 text-right">
              Last time{" "}
              <span className="text-[#f4eddb]">
                {comeback.lastFelt.before} → {comeback.lastFelt.after}
              </span>
            </span>
          ) : null}
        </div>
      ) : null}
      {onRetryFile ? (
        <div className="relative mt-1.5 text-center text-[12.5px] text-[#a99d86]">
          Couldn't load your file —{" "}
          <button
            type="button"
            onClick={onRetryFile}
            className="cursor-pointer text-[#d6a94e] underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      ) : null}

      {/* the moves */}
      <div className="relative mx-auto mt-3 grid max-h-[620px] min-h-[320px] w-full max-w-[460px] flex-1 grid-cols-2 grid-rows-[minmax(0,.68fr)_minmax(0,1.3fr)_minmax(0,.32fr)] gap-2.5">
        <Panel
          wide
          onClick={ongoing ? onContinue : onNextMission}
          label={
            nextMissionLabel ??
            (ongoing
              ? "Continue your mission"
              : sealed
                ? "Tomorrow's case is created"
                : "Do another mission")
          }
          sub={
            nextMissionHint ??
            (ongoing
              ? `${ongoing.name}. The clock is still running.`
              : sealed
                ? `For ${sealed.label.toUpperCase()}. Opens ${new Date(
                    sealed.at,
                  ).toLocaleString(undefined, {
                    weekday: "short",
                    hour: "numeric",
                    minute: "2-digit",
                  })}, and you'll get one text. Open it early and the Enemy won't be ready.`
                : "The Enemy won't wait. Neither should you.")
          }
        >
          {/* their case's laptop, close up: the Enemy's seal flickering on
              it (a new strike waiting), or, with a mission on, its clock;
              or tomorrow's case, sealed and waiting */}
          <span className="relative block w-[50%] shrink-0 overflow-hidden bg-black">
            {sealed && !ongoing ? (
              <span className="absolute inset-0 block bg-[radial-gradient(circle_at_50%_50%,#1a1206,#000_75%)] p-1.5 [container-type:size]">
                <SealedCase label={sealed.label} at={sealed.at} compact />
              </span>
            ) : null}
            <span
              className={`absolute top-1/2 left-[-20%] aspect-[1451/1084] w-[140%] -translate-y-[36%] [container-type:inline-size] ${
                sealed && !ongoing ? "hidden" : ""
              }`}
            >
              <img
                src={OPEN_CASES[ongoing?.tier ?? "beginner"]}
                alt=""
                className="absolute inset-0 size-full"
              />
              <span
                style={screenFor(ongoing?.tier ?? "beginner")}
                className="absolute overflow-hidden rounded-[.3cqw] bg-black"
              >
                {ongoing ? (
                  <MissionClock endsAt={ongoing.endsAt} />
                ) : (
                  <EnemyScreen still />
                )}
              </span>
            </span>
            <span className="absolute inset-0 bg-[linear-gradient(90deg,transparent_55%,rgba(10,11,14,.9))]" />
            {ongoing ? (
              <span className="absolute bottom-2 left-2 rounded-[3px] bg-black/70 px-1.5 py-0.5 font-mono text-[9px] tracking-[.2em] text-[#ffb547] uppercase">
                ● Live
              </span>
            ) : sealed ? (
              <span className="absolute bottom-2 left-2 rounded-[3px] bg-black/70 px-1.5 py-0.5 font-mono text-[9px] tracking-[.2em] text-[#d6a94e] uppercase">
                ● Created
              </span>
            ) : (
              <span className="absolute bottom-2 left-2 rounded-[3px] bg-black/70 px-1.5 py-0.5 font-mono text-[9px] tracking-[.2em] text-[#4ade80] uppercase">
                ● Ready
              </span>
            )}
          </span>
        </Panel>

        <Panel
          href="/key-handoff"
          label="Pass on a key"
          sub="Give a key to someone you believe in."
        >
          <span className="relative block min-h-0 flex-1 overflow-hidden bg-black">
            <span className="absolute inset-y-[10%] left-1/2 aspect-[322/1000] -translate-x-1/2 -rotate-[24deg] [container-type:size]">
              <img src={keyImg} alt="" className="block size-full" />
              {name ? (
                <span
                  style={{
                    width: "40cqh",
                    fontSize: `${Math.min(3.9, 37 / Math.max(1, name.length * 0.72))}cqh`,
                  }}
                  className="absolute top-[60%] left-1/2 -translate-1/2 -rotate-90 text-center font-stencil leading-none tracking-[.14em] whitespace-nowrap text-[#2b1a0b] uppercase"
                >
                  {name}
                </span>
              ) : null}
            </span>
          </span>
        </Panel>

        <Panel
          onClick={onLegend}
          label="Operative legend"
          sub="Every mission you've filed."
        >
          <span className="relative grid min-h-0 flex-1 place-items-center bg-[radial-gradient(circle_at_50%_45%,rgba(214,169,78,.18),#000_70%)]">
            {/* their agent badge: an ID card in gold, their engraved name
                and how many missions they have filed */}
            <span className="relative flex w-[78%] -rotate-[4deg] flex-col items-center rounded-[8px] border border-[#d6a94e]/70 bg-[linear-gradient(160deg,#2a2014,#0d0a06)] px-2 pt-2 pb-2.5 shadow-[0_0_14px_rgba(214,169,78,.35)]">
              <span className="font-mono text-[7.5px] tracking-[.3em] text-[#d6a94e] uppercase">
                Northstar agency
              </span>
              <span className="mt-1.5 grid size-8 place-items-center rounded-full border border-[#d6a94e]/60 bg-black/50 font-stencil text-[13px] text-[#d6a94e]">
                {(name || "?").slice(0, 1)}
              </span>
              <span className="mt-1 max-w-full truncate font-stencil text-[12px] tracking-[.12em] text-[#f4eddb] uppercase">
                {name || "Operative"}
              </span>
              <span className="mt-0.5 font-mono text-[8px] tracking-[.2em] text-[#a99d86] uppercase">
                Agent · {count} filed
              </span>
            </span>
          </span>
        </Panel>
        {comeback?.clearance ? (
          <Panel
            wide
            onClick={copyClearance}
            label={copied ? "Link copied" : "Clearance"}
            sub={
              copied
                ? "Send it to someone. They see your locks open, never your reports."
                : "Let someone watch your locks open. Tap to copy your link."
            }
          >
            <span className="grid w-[22%] shrink-0 place-items-center bg-black text-[#d6a94e]">
              <Link2 className="size-6" />
            </span>
          </Panel>
        ) : null}
      </div>

      {hint ? (
        <div className="absolute inset-x-0 bottom-1.5 text-center font-mono text-[10px] tracking-[.26em] text-[#a99d86] uppercase">
          Select a panel to continue
        </div>
      ) : null}
    </div>
  );
}
